import { type RefObject, useEffect } from 'react';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import {
  canvasResolvedImageReady,
  svgImageReady,
} from 'tabview/canvasRendering/canvasResolvedImages';
import type { AqBlocks } from 'tabview/visualizations/aq-blocks/types';

/**
 * aq-blocks draws icons and component images through `HTMLImageElement`s that may not
 * have loaded, and drawing an incomplete one paints nothing. Its render loop stops as
 * soon as a frame reports complete, so an image resolving after the entry animation is
 * never drawn until a hover, pan or zoom forces a repaint. The loader signals readiness
 * as an action and expects the mounting view to ask for another frame; every view that
 * mounts `<aq-blocks>` needs this.
 */
export const useRepaintOnImageReady = (
  elementRef: RefObject<Pick<AqBlocks, 'pendRender'> | null>
): void => {
  useEffect(() => {
    const repaint = () => elementRef.current?.pendRender();
    const subscriptions = [
      subscribeToAction(svgImageReady, repaint),
      subscribeToAction(canvasResolvedImageReady, repaint),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, [elementRef]);
};
